import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { Stack } from "components/layout/reusableLayouts";
import { PaymentLinkSuccessType, PaymentLinkType } from "enums/PaymentLink";
import { PublicPaymentLink } from "hooks/usePayLinkSession";
import { intervalPeriod } from "utils/billingInterval";

interface PayLinkSuccessProps {
  paymentLink?: PublicPaymentLink;
  companyName?: string;
}

const PayLinkSuccess: React.FC<PayLinkSuccessProps> = ({
  paymentLink,
  companyName,
}) => {
  const { t } = useTranslation();

  const isSubscription = paymentLink?.linkType === PaymentLinkType.SUBSCRIPTION;
  const successBehavior = paymentLink?.successBehavior;
  const redirectUrl =
    successBehavior?.type === PaymentLinkSuccessType.REDIRECT
      ? toSafeRedirect(successBehavior.redirectUrl)
      : undefined;

  useEffect(() => {
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    }
  }, [redirectUrl]);

  if (redirectUrl) {
    return (
      <>
        <Typography variant="body1" sx={{ textAlign: "center" }}>
          {t("payLink.Redirecting")}
        </Typography>
      </>
    );
  }

  return (
    <>
      <Stack sx={{ gap: "var(--space-3)", textAlign: "center" }}>
        <Typography variant="h5">
          {isSubscription
            ? t("payLink.SubscriptionStartedTitle")
            : t("payLink.ThankYouTitle")}
        </Typography>
        <Typography variant="body1">
          {successBehavior?.message || headline()}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "var(--color-text-secondary)" }}
        >
          {isSubscription
            ? t("payLink.SubscriptionCancelAnytime")
            : t("payLink.ReceiptOnItsWay")}
        </Typography>
      </Stack>
    </>
  );

  function headline() {
    if (!isSubscription) {
      return companyName
        ? t("payLink.ThankYouNamed", { companyName })
        : t("payLink.ThankYouMessage");
    }

    const interval = t(intervalPeriod(paymentLink?.interval));

    return companyName
      ? t("payLink.SubscriptionStartedNamed", { companyName, interval })
      : t("payLink.SubscriptionStartedMessage", { interval });
  }
};

/* Allow only web URLs because this merchant-controlled redirect runs on our public origin. */
function toSafeRedirect(url?: string): string | undefined {
  if (!url) return undefined;

  try {
    const parsed = new URL(url, window.location.origin);

    return parsed.protocol === "https:" || parsed.protocol === "http:"
      ? parsed.toString()
      : undefined;
  } catch (error) {
    return undefined;
  }
}

export default PayLinkSuccess;
